import { useCallback, useMemo } from "react";

import type {
  DispatchViewStatePatch,
  ResolvedDispatchPageSettings,
  ResolvedDispatchView,
} from "@storyboard/dispatch-view/resolve";
import { resolveDispatchPageSettings, resolveDispatchView } from "@storyboard/dispatch-view/resolve";
import type { DispatchViewDefinition, DispatchViewState } from "@storyboard/dispatch-view/view";
import { allDispatchColumns } from "@/components/Dispatch/columns";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import type { TagDefinition } from "@/types/driver";
import { useDispatchView } from "./useDispatchView";
import { useDispatchViewState } from "./useDispatchViewState";

/** The page's settings in force and the switch a member flips from the page. */
export type DispatchPageControls = ResolvedDispatchPageSettings & {
  setShowShortcuts: (shown: boolean) => void;
};

/**
 * The view the page renders from, already reconciled: the active definition (from `?view=`), the member's state
 * over it, and the page's settings, resolved once through `@storyboard/dispatch-view/resolve` so no call site lays
 * state over a definition by hand.
 *
 * `resolved` is `null` while the cascade offers no view at all. `state` is the compiled entry the view was resolved
 * with, for a control that shows what the member chose rather than what changed. `write` and `reset` address the
 * active view's state entry with a sparse patch, and the chips read what the compiled state changes about the view,
 * whoever wrote it. `page` carries the keymap, the shortcut bar switch, and the phone tag's slug.
 *
 * @param tags - The workspace's tags the member may show as columns; the offered column set is the view's columns
 *   first, then the other driver fields, then these by label.
 */
export function useResolvedDispatchView(tags: readonly TagDefinition[]): {
  definition: DispatchViewDefinition | null;
  views: DispatchViewDefinition[];
  selectView: (viewId: string) => void;
  resolved: ResolvedDispatchView | null;
  state: DispatchViewState | undefined;
  write: (patch: DispatchViewStatePatch) => void;
  reset: () => void;
  page: DispatchPageControls;
} {
  const { settings, updateWebSettings } = useWebSettings();
  const { view: definition, views, selectView } = useDispatchView();
  const { state, write, reset } = useDispatchViewState(definition?.id);

  const tagsBySlug = useMemo(() => new Map(tags.map((tag) => [tag.slug, tag])), [tags]);
  const resolved = useMemo(
    () =>
      definition ? resolveDispatchView(definition, state, allDispatchColumns(definition, tags, tagsBySlug)) : null,
    [definition, state, tags, tagsBySlug],
  );

  const { keymap, showShortcuts, phoneTagSlug } = settings.dispatch;
  const pageSettings = useMemo(
    () => resolveDispatchPageSettings({ keymap, showShortcuts, phoneTagSlug: phoneTagSlug ?? undefined }),
    [keymap, showShortcuts, phoneTagSlug],
  );
  const setShowShortcuts = useCallback(
    (shown: boolean) => updateWebSettings({ dispatch: { showShortcuts: shown } }),
    [updateWebSettings],
  );
  const page = useMemo<DispatchPageControls>(
    () => ({ ...pageSettings, setShowShortcuts }),
    [pageSettings, setShowShortcuts],
  );

  return { definition, views, selectView, resolved, state, write, reset, page };
}
